import { defineStore, getActivePinia, type Store } from "pinia";
import mixpanel from "mixpanel-browser";
import { nextTick } from "vue";
import { forcedLoginTimestamp } from "@/constants";
import router from "@/router";
import {
    getMasqueradeAppUrl,
    handleError,
    safeResetStore,
    shouldSkipNewBuyerAppRedirect,
} from "@/utils";
import UserService from "@/services/User";
import CompanyService from "@/services/Company";
import SupplierService from "@/services/Supplier";
import MasqueradeService from "@/services/Masquerade";
import type { ISigninError } from "@/types/Error";
import type IUserInformation from "@/types/UserInformation";
import { abortAllRequests } from "@/services/axiosInstance";
import formatErrorsForForm from "@/utils/formatErrorsForForm";
import type ICompanyInformation from "@/types/CompanyInformation";
import type {
    CompanyAddress,
    AddressTypeEnum,
} from "@/types/api/data-contracts";
import { CompanyRoleEnum, CompanyTypeEnum } from "@/types/api/data-contracts";
import { useFeatureFlag } from "@/composables";

const LAST_LOGIN_KEY = "last_login";
const FORCED_LOGIN_DATE = new Date(forcedLoginTimestamp);

export const useAuthStore = defineStore({
    id: "userAuth",
    state: () => ({
        booting: false,
        pageLoading: false,
        loading: false,
        loaded: false,
        auth: false,
        masqueradeMode: false,
        masqueradeOriginalToken: null as string | null,
        masqueradeTargetUserEmail: null as string | null,
        masqueradeTargetCompanyName: null as string | null,
        masqueradeTargetCompanyType: null as CompanyTypeEnum | null,
        errors: [] as ISigninError[],
        token: localStorage.getItem("userAuthToken") || null,
        companyData: null as unknown as ICompanyInformation,
        companyDeliveryAddresses: [] as CompanyAddress[],
        companyDispatchAddresses: [] as CompanyAddress[],
        userData: null as unknown as IUserInformation,
        supplierService: SupplierService.getInstance(),
        userService: UserService.getInstance(),
        companyService: CompanyService.getInstance(),
        masqueradeService: MasqueradeService.getInstance(),
        oauthToken: null as string | null,
    }),
    getters: {
        isLoggedIn: (state) => state.token !== null,
        isMerchantOfRecord: (state) =>
            state?.companyData?.company_type === "merchant_of_record",
        isSupplier: (state) => state?.companyData?.company_type === "supplier",
        isBrand: (state) => state?.companyData?.company_type === "brand",
        userCompanyType: (state) => state?.companyData?.company_type,
        getReferralCode: (state) => state?.companyData?.referral_code,
        companyAddress: (state) => state?.companyData?.company_addresses,
        getUserUUID: (state) => state?.userData?.uuid,
        getUserOAuthToken: (state) => state?.oauthToken ?? null,
        isCompanyAdmin: (state) =>
            state?.userData?.role === CompanyRoleEnum.Admin,
        isCompanyStaff: (state) =>
            state?.userData?.role === CompanyRoleEnum.Staff,
    },
    actions: {
        checkLastLoginTimestamp() {
            const lastLogin = localStorage.getItem(LAST_LOGIN_KEY);
            if (!lastLogin || new Date(lastLogin) < FORCED_LOGIN_DATE) {
                this.signout();

                return false;
            }

            return true;
        },
        /**
         * Fetch canny token and redirect user if `route.query` contains
         * companyID and redirect
         */
        async fetchCannyToken() {
            const { companyID, redirect } = router.currentRoute.value.query;
            if (companyID && redirect) {
                const response = await this.userService.getCannySSOToken();
                const { token } = response.data;
                const redirectUrl = `https://canny.io/api/redirects/sso?companyID=${companyID}&ssoToken=${token}&redirect=${redirect}`;
                window.open(redirectUrl, "_blank")?.focus();
            }
        },
        async fetchUserOAuthToken() {
            try {
                const response = await this.userService.getUserOAuthToken();
                const { access_token: accessToken } = response.data;
                this.oauthToken = accessToken;
            } catch (err) {
                handleError(err);
                this.oauthToken = null;
            }
        },
        async fetchCompanyAddresses(
            addressType: "delivery_address" | "dispatch_address"
        ) {
            const { data } = await this.companyService.getAddresses(
                1,
                addressType as `${AddressTypeEnum}`
            );

            if (addressType === "delivery_address") {
                this.companyDeliveryAddresses = data.results || [];
            } else if (addressType === "dispatch_address") {
                this.companyDispatchAddresses = data.results || [];
            }
        },
        async fetchDeliveryAddresses() {
            await this.fetchCompanyAddresses("delivery_address");
        },
        async fetchDispatchAddresses() {
            await this.fetchCompanyAddresses("dispatch_address");
        },
        async fetchAddresses() {
            await this.fetchDeliveryAddresses();
            await this.fetchDispatchAddresses();
        },
        async authenticateUser() {
            try {
                const lastLoginTimestampIsValid =
                    this.checkLastLoginTimestamp();

                if (!lastLoginTimestampIsValid) return;

                this.booting = true;
                // fetch company information
                const response = await this.companyService.getInfo();
                this.companyData = response.data;
                // fetch user information
                const userResponse = await this.userService.getInfo();
                this.userData = userResponse.data;
                // fetch canny token
                await this.fetchCannyToken();

                // Fetch company addresses
                await this.fetchAddresses();
                // set state in store
                this.auth = true;
                this.loading = false;
                this.booting = false;
                this.loaded = true;
            } catch (err) {
                this.loading = false;
                this.booting = false;
                this.loaded = true;
            }
        },
        setLastLoginTimestamp() {
            localStorage.setItem(LAST_LOGIN_KEY, new Date().toISOString());
        },
        async setTokenAndAuthenticate(token: string) {
            this.token = token;
            localStorage.setItem("userAuthToken", this.token);
            this.setLastLoginTimestamp();
            await this.authenticateUser();
        },
        setMasqueradeModel() {
            this.masqueradeMode = true;
        },
        /**
         * Start MoR masquerade session for a target user.
         * Stores original MoR token for later restoration.
         */
        async startMasquerade(
            targetUserUuid: string,
            masqueradeTab?: Window | null
        ) {
            try {
                this.loading = true;

                // Get temporary token from MoR endpoint
                const tempResponse =
                    await this.masqueradeService.getTemporaryToken(
                        targetUserUuid
                    );

                const tempToken = tempResponse.data.token;
                const targetType = tempResponse.data.target_company_type;
                const targetEmail = tempResponse.data.target_user_email;
                const targetCompany = tempResponse.data.target_company_name;

                // TODO: remove `masqueradeToNewBuyer` flag and old in-app brand masquerade path once the new buyer app is fully adopted
                const { isFeatureFlagEnabled } = useFeatureFlag();
                if (
                    targetType === CompanyTypeEnum.Brand &&
                    isFeatureFlagEnabled("masqueradeToNewBuyer") &&
                    !shouldSkipNewBuyerAppRedirect()
                ) {
                    // SC-3953 feature toggle: the isolated-origin masquerade is
                    // gated behind `masqueradeIsolatedOrigin`. While it's OFF we
                    // keep the LEGACY same-origin handoff so production behaves
                    // exactly as before — the buyer app on its MAIN origin (in
                    // the current tab) backs up the MoR token and restores it on
                    // exit. Flip the flag ON (once the masquerade subdomain is
                    // provisioned) to switch to the isolated-origin path below.
                    // TODO(SC-3953 cleanup): revert this coexistence commit once
                    // the isolated-origin masquerade is fully rolled out.
                    if (!isFeatureFlagEnabled("masqueradeIsolatedOrigin")) {
                        masqueradeTab?.close();
                        const buyerAppUrl = import.meta.env
                            .VITE_NEW_BUYER_APP_URL;
                        if (!buyerAppUrl) {
                            this.loading = false;
                            handleError(
                                new Error("Missing VITE_NEW_BUYER_APP_URL")
                            );
                            return;
                        }
                        const legacyUrl = new URL("/masquerade", buyerAppUrl);
                        legacyUrl.searchParams.set("token", tempToken);
                        legacyUrl.searchParams.set("email", targetEmail);
                        legacyUrl.searchParams.set("company", targetCompany);
                        window.location.href = legacyUrl.toString();
                        this.loading = false;
                        return;
                    }

                    // Hand off to the new buyer app on its ISOLATED masquerade
                    // origin (masquerade.<host>) so the exchanged buyer token
                    // lands in that origin's own localStorage and never clobbers
                    // the MoR session on the buyer-app origin. Open in a NEW TAB
                    // so the MoR view and the buyer view stay usable side by
                    // side. (SC-3953)
                    const masqueradeBaseUrl = getMasqueradeAppUrl();
                    if (!masqueradeBaseUrl) {
                        masqueradeTab?.close();
                        this.loading = false;
                        handleError(
                            new Error("Missing VITE_NEW_BUYER_APP_URL")
                        );
                        return;
                    }
                    const masqueradeUrl = new URL(
                        "/masquerade",
                        masqueradeBaseUrl
                    );
                    masqueradeUrl.searchParams.set("token", tempToken);
                    masqueradeUrl.searchParams.set("email", targetEmail);
                    masqueradeUrl.searchParams.set("company", targetCompany);
                    const href = masqueradeUrl.toString();
                    // The tab was opened synchronously in the click handler to
                    // dodge popup blockers; navigate it now. If it was blocked
                    // at click time, try a fresh open, and only fall back to the
                    // current tab if that is also blocked. No "noopener" here:
                    // window.open() returns null when a popup is blocked, but it
                    // ALSO returns null on SUCCESS when "noopener" is set — which
                    // would misfire the current-tab fallback on every open. The
                    // click-handler tab is opened without noopener too, so this
                    // is consistent. (Aliased to a local so we don't mutate a
                    // function param.)
                    const targetTab = masqueradeTab;
                    if (targetTab && !targetTab.closed) {
                        targetTab.location.href = href;
                    } else if (!window.open(href, "_blank")) {
                        window.location.href = href;
                    }
                    this.loading = false;
                    return;
                }

                // Non-brand target / flag off → legacy in-app masquerade in the
                // current tab; close the tab the click handler pre-opened.
                masqueradeTab?.close();

                // Store original MoR token before modifying session state
                this.masqueradeOriginalToken = this.token;

                // Store target info for banner
                this.masqueradeTargetUserEmail = targetEmail;
                this.masqueradeTargetCompanyName = targetCompany;
                this.masqueradeTargetCompanyType = targetType;

                // Exchange for real token (reuse admin endpoint)
                const tokenResponse =
                    await this.userService.getUserTokenForMasqueradeMode(
                        tempToken
                    );

                // Navigate FIRST to unmount MoR-specific components before token switch
                // This prevents MoR endpoints from being called with brand/supplier token
                // when authenticateUser() triggers component re-mounts
                const dashboardRoute =
                    targetType === CompanyTypeEnum.Supplier
                        ? "Supplier Dashboard"
                        : "Brand Dashboard";
                await router.push({ name: dashboardRoute });

                // THEN set masquerade mode and authenticate
                this.masqueradeMode = true;
                await this.setTokenAndAuthenticate(tokenResponse.data.token);
                this.identifyUserOnMixpanel(true);

                this.loading = false;
            } catch (err) {
                // Never leave the pre-opened tab stranded on about:blank.
                masqueradeTab?.close();
                // Restore original token on failure
                if (this.masqueradeOriginalToken) {
                    this.token = this.masqueradeOriginalToken;
                    localStorage.setItem("userAuthToken", this.token);
                }
                this.masqueradeOriginalToken = null;
                this.masqueradeTargetUserEmail = null;
                this.masqueradeTargetCompanyName = null;
                this.masqueradeTargetCompanyType = null;
                this.loading = false;
                handleError(err);
            }
        },
        /**
         * End MoR masquerade session and restore original MoR token.
         */
        async endMasquerade() {
            if (!this.masqueradeOriginalToken) return;

            try {
                this.loading = true;

                // Restore original token and reset state
                const originalToken = this.masqueradeOriginalToken;
                this.masqueradeMode = false;
                this.masqueradeOriginalToken = null;
                this.masqueradeTargetUserEmail = null;
                this.masqueradeTargetCompanyName = null;
                this.masqueradeTargetCompanyType = null;

                // Re-authenticate with MoR token
                await this.setTokenAndAuthenticate(originalToken);
                this.identifyUserOnMixpanel(false);

                // Redirect back to MoR dashboard
                router.push({ name: "Merchant of Record Dashboard" });

                this.loading = false;
            } catch (err) {
                this.loading = false;
                handleError(err);
            }
        },
        identifyUserOnMixpanel(signedInWithMasqueradeMode = false) {
            mixpanel.register({
                signed_in_with_masquerade_mode: signedInWithMasqueradeMode,
            });

            mixpanel.identify(this.userData.uuid);
            mixpanel.people.set({
                $email: this.userData.email,
                $first_name: this.userData.first_name,
                $last_name: this.userData.last_name,
                company_uuid: this.companyData?.uuid || "Unknown",
                company_name: this.companyData?.name || "Unknown",
                company_type: this.companyData?.company_type || "Unknown",
            });
        },
        async signupSupplier(payload: object) {
            try {
                this.loading = true;
                const { data } = await this.supplierService.onBoard(payload);
                const { token: authToken } = data;
                this.token = authToken;
                localStorage.setItem("userAuthToken", authToken);
                this.setLastLoginTimestamp();
            } catch (err: any) {
                this.loading = false;
                const errors = err?.response?.data ?? null;
                if (errors) {
                    this.errors = formatErrorsForForm(errors);
                }
                return;
            }
            this.authenticateUser();
        },

        async signin(payload: object) {
            try {
                this.loading = true;
                const response = await this.userService.signin(payload);
                const { token: authToken } = await response.data;
                this.token = authToken;
                localStorage.setItem("userAuthToken", authToken);
                this.setLastLoginTimestamp();
                await this.authenticateUser();
                if (window.zE && this.companyData?.zendesk_chat_token) {
                    window.zE("messenger", "show");
                }

                this.identifyUserOnMixpanel();
            } catch (err: any) {
                this.loading = false;
                const errors = err?.response?.data ?? [];
                // we need to show the sign in errors inside the form instead of a toast
                if (errors) {
                    this.errors = formatErrorsForForm(errors);
                }
            }
        },
        async signout() {
            if (window.zE && this.companyData?.zendesk_chat_token) {
                await window.zE("messenger", "logoutUser");
                window.zE("messenger", "hide");
            }

            // Step 1: show app loader
            this.pageLoading = true;

            // Step 2: Reset all active Pinia stores
            const activePinia = getActivePinia();
            if (activePinia) {
                // for some reason Pinia type does not include _s in its type definition
                // @ts-ignore
                activePinia._s.forEach((store: Store) => {
                    safeResetStore(store);
                });
            }

            // Step 3: Abort any ongoing Axios network calls
            abortAllRequests();

            // Step 4: Remove token and reset auth state
            this.token = null;
            this.auth = false;
            localStorage.removeItem("userAuthToken");

            // Wait for state updates to complete
            await nextTick();

            // Step 5: Redirect user to sign-in page
            router.push({ name: "Signin" });

            // Step 6: hide app loader
            this.pageLoading = false;

            // Step 7: reset Mixpanel identity
            mixpanel.reset();
        },
    },
    persist: {
        paths: [
            "masqueradeMode",
            "masqueradeOriginalToken",
            "masqueradeTargetUserEmail",
            "masqueradeTargetCompanyName",
            "masqueradeTargetCompanyType",
        ],
    },
});

export default { useAuthStore };
